import type { Rect, Size } from "../../core/services/scan-image";

/** A trading card is 2.5 by 3.5 inches. */
export const CARD_ASPECT = 3.5 / 2.5;

/** Where the guide sits from the top of the preview, under the top bar. */
export const GUIDE_TOP = 76;

/** What the prompt and the bottom panel take below the guide, when the panel is not measured. */
const GUIDE_RESERVED_BELOW = 330;

/** What the prompt under the guide takes, with its gaps. */
const PROMPT_ROOM = 48;

/** The smallest guide drawn with the prompt under it: in a shorter room the prompt goes inside the guide. */
const GUIDE_MIN_HEIGHT = 160;

/** The smallest guide drawn at all. */
const GUIDE_LEAST = 96;

/** The prompt inside the guide, over its bottom edge. */
const PROMPT_INSIDE = 12;

/** What the panel takes under the camera with cards in its tray, the app's prompt and a notice. */
const PANEL_WITH_TRAY = 340;

/** The panel beside the camera: `w-[min(340px,48%)]`. */
const PANEL_BESIDE = 340;

/**
 * The frame guide in the preview: card-shaped, 76% of the room's width, centred in it, its top under the top bar,
 * shrunk to stay in the room with the prompt under it; in a room too short for that, the prompt goes inside the guide.
 * `room` is the part of the preview the panel leaves: above it, or beside it (`panelBeside`). `scale` is the root
 * font's size over 16 px: a browser set to larger text grows the top bar and the prompt with it.
 */
export function guideLayout(
  preview: Size,
  room: Size = { width: preview.width, height: preview.height - GUIDE_RESERVED_BELOW + PROMPT_ROOM },
  scale = 1,
): { guide: Rect; promptInside: boolean } {
  const top = GUIDE_TOP * scale;
  const widest = room.width * 0.76 * CARD_ASPECT;
  const below = room.height - top - PROMPT_ROOM * scale;
  const promptInside = below < Math.min(widest, GUIDE_MIN_HEIGHT);
  const fit = promptInside ? Math.max(GUIDE_LEAST, room.height - top - PROMPT_INSIDE) : below;
  const height = Math.min(widest, fit);
  const width = height / CARD_ASPECT;
  return { guide: { x: (room.width - width) / 2, y: top, width, height }, promptInside };
}

export const guideRect = (preview: Size, room?: Size): Rect => guideLayout(preview, room).guide;

/**
 * Where the prompt goes, as wide as the room so it keeps to one line where it can: its top 14 px under the guide, or,
 * inside the guide, its last line 12 px over the guide's bottom edge, a longer prompt growing upwards (`y` is then where
 * its bottom edge sits).
 */
export function promptPlace(
  guide: Rect,
  promptInside: boolean,
  roomWidth: number,
): { y: number; width: number; growsUp: boolean } {
  return promptInside
    ? { y: guide.y + guide.height - 12, width: roomWidth, growsUp: true }
    : { y: guide.y + guide.height + 14, width: roomWidth, growsUp: false };
}

/**
 * The panel goes beside the camera on a preview wider than tall and too short to keep a guide above it with cards in
 * the tray: a phone held sideways, a short window. iOS is held upright only.
 */
export function panelBeside(preview: Size, scale = 1): boolean {
  return (
    preview.width > preview.height &&
    preview.height < (GUIDE_TOP + PROMPT_ROOM + PANEL_WITH_TRAY) * scale + GUIDE_MIN_HEIGHT
  );
}

/** The room the panel beside the camera leaves, known from its width before it is laid out. */
export const roomBeside = (preview: Size): Size => ({
  width: preview.width - Math.min(PANEL_BESIDE, preview.width * 0.48),
  height: preview.height,
});
